/**
 * Passkey sign-in driver for the landing page.
 *
 * Reveals the passkey button when the browser supports WebAuthn, runs
 * the assertion ceremony against /v1/webauthn-authentication, then
 * exchanges the resulting JWT for a PHP session via the
 * /app/auth/passkey-login bridge so the user lands authenticated on
 * the destination page.
 */

const PASSKEY_BUTTON_ID = 'passkey-signin-btn';
const PASSKEY_ERROR_ID = 'passkey-signin-error';
const BRIDGE_PATH = '/app/auth/passkey-login';
let errorTimer = null;
let conditionalAbortController = null;
let conditionalRequest = null;

function helpers() {
    return window.WebAuthnHelpers;
}

function isWebAuthnSupported() {
    return helpers().isWebAuthnSupported();
}

function translate(domain, key, fallback) {
    return helpers().translate(domain, key, fallback);
}

function base64UrlEncode(bytes) {
    return helpers().base64UrlEncode(bytes);
}

function decodeRequestOptions(publicKey) {
    return helpers().decodeRequestOptions(publicKey);
}

function showError(text, autoClearMs = 0) {
    const errEl = document.getElementById(PASSKEY_ERROR_ID);
    if (!errEl) return;
    if (errorTimer) {
        clearTimeout(errorTimer);
        errorTimer = null;
    }
    errEl.textContent = text;
    errEl.classList.remove('hidden');
    if (autoClearMs > 0) {
        errorTimer = window.setTimeout(() => {
            clearError();
        }, autoClearMs);
    }
}

function clearError() {
    const errEl = document.getElementById(PASSKEY_ERROR_ID);
    if (!errEl) return;
    if (errorTimer) {
        clearTimeout(errorTimer);
        errorTimer = null;
    }
    errEl.textContent = '';
    errEl.classList.add('hidden');
}

function isLocalhostHost() {
    const host = window.location?.hostname || '';
    return host === 'localhost' || host === '127.0.0.1' || host === '::1';
}

function passkeyHostMismatchMessage() {
    return translate(
        'auth',
        'passkey_error_localhost',
        'Passkeys cannot be used from localhost. Open the Cloudflare tunnel URL for this branch and try again.'
    );
}

function passkeyRejectedMessage() {
    return translate(
        'auth',
        'passkey_error_rejected',
        'This passkey could not be verified. Try another passkey or sign in with your password.'
    );
}

async function cancelConditionalRequest() {
    if (!conditionalAbortController) {
        return;
    }

    const pendingRequest = conditionalRequest;
    conditionalAbortController.abort();
    conditionalAbortController = null;
    conditionalRequest = null;

    if (pendingRequest) {
        try {
            await pendingRequest;
        } catch (_e) {
            // Aborting the conditional request is expected before the
            // explicit passkey button starts its own WebAuthn ceremony.
        }
    }
}

function makeApiError(message, status) {
    const err = new Error(message);
    err.status = status;
    return err;
}

async function fetchOptions() {
    const body = new FormData();
    const res = await fetch('/v1/webauthn-authentication/options', {
        method: 'POST',
        body,
        headers: { Accept: 'application/json' },
        credentials: 'omit',
    });
    if (!res.ok) {
        throw makeApiError('options_failed', res.status);
    }
    return res.json();
}

async function postAssertion(payload) {
    const body = new FormData();
    Object.entries(payload).forEach(([k, v]) => {
        if (v !== undefined && v !== null) {
            body.append(k, v);
        }
    });
    const res = await fetch('/v1/webauthn-authentication', {
        method: 'POST',
        body,
        headers: { Accept: 'application/json' },
        credentials: 'omit',
    });
    if (!res.ok) {
        throw makeApiError('assertion_failed', res.status);
    }
    return res.json();
}

/**
 * Hand the JWT to the legacy session bridge so Twig pages render
 * authenticated. The browser is then redirected to the desired page.
 * The CSRF token is pulled from the meta tag the auth layout exposes —
 * the base controller's CSRF check rejects POSTs that lack it.
 */
function submitToBridge(token, redirectUrl) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = BRIDGE_PATH;
    form.style.display = 'none';

    const csrfToken = document.querySelector('meta[name="csrf_token"]')?.content
        || document.querySelector('meta[name="csrf-token"]')?.content
        || '';
    if (csrfToken) {
        const csrfField = document.createElement('input');
        csrfField.type = 'hidden';
        csrfField.name = 'csrf_token';
        csrfField.value = csrfToken;
        form.appendChild(csrfField);
    }

    const tokenField = document.createElement('input');
    tokenField.type = 'hidden';
    tokenField.name = 'token';
    tokenField.value = token;
    form.appendChild(tokenField);

    if (redirectUrl) {
        const redirField = document.createElement('input');
        redirField.type = 'hidden';
        redirField.name = 'redirect';
        redirField.value = redirectUrl;
        form.appendChild(redirField);
    }

    document.body.appendChild(form);
    form.submit();
}

async function runCeremony(button) {
    button.disabled = true;
    clearError();
    try {
        if (isLocalhostHost()) {
            showError(passkeyHostMismatchMessage());
            return;
        }

        await cancelConditionalRequest();

        const options = await fetchOptions();
        const decoded = decodeRequestOptions(options.publicKey);

        let assertion;
        try {
            assertion = await navigator.credentials.get({ publicKey: decoded });
        } catch (e) {
            if (e && (e.name === 'NotAllowedError' || e.name === 'AbortError')) {
                const t = translate(
                    'auth',
                    'passkey_error_cancelled',
                    'Cancelled. Try again or use your password.'
                );
                showError(t, 4000);
                return;
            }
            throw e;
        }

        if (!assertion) {
            return;
        }

        const result = await postAssertion({
            challengeId: options.challengeId,
            credentialId: base64UrlEncode(assertion.rawId),
            clientDataJSON: base64UrlEncode(assertion.response.clientDataJSON),
            authenticatorData: base64UrlEncode(assertion.response.authenticatorData),
            signature: base64UrlEncode(assertion.response.signature),
            userHandle: assertion.response.userHandle
                ? base64UrlEncode(assertion.response.userHandle)
                : null,
        });

        const redirectUrl = button.dataset.passkeyRedirect || '';
        submitToBridge(result.token, redirectUrl);
    } catch (e) {
        let message = translate(
            'auth',
            'passkey_error_generic',
            'Could not complete passkey sign-in. Please try again.'
        );
        if (e && e.status === 429) {
            message = translate(
                'auth',
                'passkey_error_rate_limited',
                'Too many attempts. Wait a moment and try again.'
            );
        } else if (e && (e.status === 401 || e.status === 403)) {
            message = passkeyRejectedMessage();
        }
        showError(message);
        // Surface the underlying name for debugging without leaking
        // sensitive details to the user.
        if (window.console && e && e.name) {
            console.warn('passkey-login:', e.name, e.message);
        }
    } finally {
        button.disabled = false;
    }
}

function init() {
    if (!isWebAuthnSupported()) {
        return;
    }
    const button = document.getElementById(PASSKEY_BUTTON_ID);
    if (!button) {
        return;
    }
    button.classList.remove('hidden');
    button.addEventListener('click', () => { void runCeremony(button); });

    if (isLocalhostHost()) {
        return;
    }

    // Conditional UI: kick off a non-blocking authentication when the
    // browser supports it. The user just focuses the username field
    // and the OS offers their saved passkey inline.
    const cm = window.PublicKeyCredential;
    if (typeof cm.isConditionalMediationAvailable === 'function') {
        cm.isConditionalMediationAvailable().then(async (available) => {
            if (!available) return;
            try {
                const options = await fetchOptions();
                const decoded = decodeRequestOptions(options.publicKey);
                conditionalAbortController = new AbortController();
                conditionalRequest = navigator.credentials.get({
                    publicKey: decoded,
                    mediation: 'conditional',
                    signal: conditionalAbortController.signal,
                });
                const assertion = await conditionalRequest;
                conditionalAbortController = null;
                conditionalRequest = null;
                if (!assertion) return;
                const result = await postAssertion({
                    challengeId: options.challengeId,
                    credentialId: base64UrlEncode(assertion.rawId),
                    clientDataJSON: base64UrlEncode(assertion.response.clientDataJSON),
                    authenticatorData: base64UrlEncode(assertion.response.authenticatorData),
                    signature: base64UrlEncode(assertion.response.signature),
                    userHandle: assertion.response.userHandle
                        ? base64UrlEncode(assertion.response.userHandle)
                        : null,
                });
                submitToBridge(result.token, button.dataset.passkeyRedirect || '');
            } catch (e) {
                conditionalAbortController = null;
                conditionalRequest = null;
                if (e && e.name === 'AbortError') {
                    return;
                }
                // Conditional UI errors are silent — the explicit button
                // remains available for retry.
                if (window.console && e && e.name) {
                    console.warn('passkey-login conditional:', e.name);
                }
            }
        }).catch(() => {});
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
